import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, AlertTriangle, DollarSign, Flame, X, Users, MapPin } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

const TORIBIO_PHOTO = 'https://customer-assets-cm19k8pv.emergentagent.net/job_killing-over-water/artifacts/g4e6rkdn_1426B748-A180-4338-9F16-39392E5C61F1.png';

const SCENE_IMAGES = {
  patrol: 'https://static.prod-images.emergentagent.com/jobs/9c53f98e-4805-48d9-9ea7-4a3815c7b3b1/images/ee6cd1f00f7460734660ca201a3a7796f07184e02984a534731fbc8ede1cac9c.jpeg',
  surveillance: 'https://static.prod-images.emergentagent.com/jobs/9c53f98e-4805-48d9-9ea7-4a3815c7b3b1/images/2f804bf051bb34d5cd5c41aff36dd5fc0c34a05842b4bd012a07b2971679d8b6.jpeg',
  interrogation: 'https://static.prod-images.emergentagent.com/jobs/9c53f98e-4805-48d9-9ea7-4a3815c7b3b1/images/383b65e2758dc8a8328d5d1798ff7e2964e97fc882cdb5a6f2c65732eb2822f6.jpeg',
};

const ESCALATION_STYLE = {
  ROUTINE: { border: '#8B1A1A', glow: '', badge: 'bg-[#8B1A1A]/20 border-[#8B1A1A]/40 text-[#E8E0D0]/70' },
  HEIGHTENED: { border: '#FF6B35', glow: 'shadow-[0_0_30px_rgba(255,107,53,0.15)]', badge: 'bg-[#FF6B35]/20 border-[#FF6B35]/50 text-[#FF6B35]' },
  AGGRESSIVE: { border: '#EF4444', glow: 'shadow-[0_0_40px_rgba(239,68,68,0.2)]', badge: 'bg-[#EF4444]/20 border-[#EF4444]/50 text-[#EF4444] animate-pulse' },
  RELENTLESS: { border: '#DC2626', glow: 'shadow-[0_0_60px_rgba(220,38,38,0.3)]', badge: 'bg-[#DC2626]/30 border-[#DC2626]/60 text-[#DC2626] animate-pulse' },
};

const PoliceTrapPopup = ({ trapData, gameId, playerCash, onResolved }) => {
  const [choosing, setChoosing] = useState(false);
  const [chosenAction, setChosenAction] = useState(null);
  const [result, setResult] = useState(null);

  if (!trapData) return null;

  const canAfford = playerCash >= trapData.bribe_cost;
  const sceneImg = SCENE_IMAGES[trapData.scene] || SCENE_IMAGES.patrol;
  const escLevel = trapData.escalation_level || 'ROUTINE';
  const style = ESCALATION_STYLE[escLevel] || ESCALATION_STYLE.ROUTINE;
  const isEscalated = escLevel !== 'ROUTINE';
  const refusals = trapData.refusals || 0;

  const handleChoice = async (choice) => {
    if (choosing) return;
    setChoosing(true);
    setChosenAction(choice);
    // Brief delay so user sees which button they pressed
    await new Promise(r => setTimeout(r, 1200));
    try {
      const res = await fetch(`${API}/api/game/${gameId}/police-trap/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ choice }),
      });
      if (res.ok) {
        const data = await res.json();
        setResult({
          type: data.trap_result?.type || choice,
          narrator: data.trap_result?.narrator || '',
          bribe_cost: data.trap_result?.bribe_cost || 0,
          heat_added: data.trap_result?.heat_added || 0,
          escalation: data.trap_result?.escalation || 'ROUTINE',
          consequences: data.trap_result?.consequences || [],
          gameState: data,
        });
        setTimeout(() => {
          onResolved(data);
        }, 3500);
      }
    } catch (e) {
      console.error('Police trap response failed:', e);
    }
    setChoosing(false);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[10000] flex items-center justify-center"
        data-testid="police-trap-overlay"
      >
        {/* Backdrop with scene image */}
        <div className="absolute inset-0">
          <img src={sceneImg} alt="" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/85 to-[#050505]/60" />
          {/* Red/blue police flash — more intense at higher escalation */}
          <motion.div
            animate={{ opacity: escLevel === 'RELENTLESS' ? [0, 0.25, 0, 0.2, 0] : escLevel === 'AGGRESSIVE' ? [0, 0.2, 0, 0.15, 0] : [0, 0.15, 0, 0.1, 0] }}
            transition={{ duration: escLevel === 'RELENTLESS' ? 1.2 : 2, repeat: Infinity, ease: 'linear' }}
            className="absolute inset-0 bg-red-600/10"
          />
          <motion.div
            animate={{ opacity: escLevel === 'RELENTLESS' ? [0, 0.2, 0, 0.25, 0] : [0, 0.1, 0, 0.15, 0] }}
            transition={{ duration: escLevel === 'RELENTLESS' ? 1.2 : 2, repeat: Infinity, ease: 'linear', delay: 0.5 }}
            className="absolute inset-0 bg-blue-600/10"
          />
        </div>

        {/* Content */}
        <motion.div
          initial={{ scale: 0.9, y: 30, opacity: 0 }}
          animate={{ scale: 1, y: 0, opacity: 1 }}
          transition={{ delay: 0.2, type: 'spring', damping: 25 }}
          className="relative z-10 max-w-md w-full mx-4"
        >
          {!result ? (
            <>
              {/* Commissioner badge header */}
              <motion.div
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="text-center mb-4"
              >
                <div className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full border ${style.badge}`}>
                  <Shield size={14} />
                  <span className="font-['Outfit'] text-[10px] tracking-[0.3em]">{trapData.title}</span>
                  <Shield size={14} />
                </div>
                {/* Escalation warning */}
                {isEscalated && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                    className="mt-2"
                  >
                    <span className={`font-['Outfit'] text-[9px] tracking-widest px-3 py-1 rounded border ${style.badge}`}
                      data-testid="escalation-badge">
                      THREAT LEVEL: {escLevel} {refusals > 0 && `(${refusals} REFUSAL${refusals !== 1 ? 'S' : ''})`}
                    </span>
                  </motion.div>
                )}
              </motion.div>

              {/* Commissioner portrait + card */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className={`bg-[#0a0e12] rounded-xl overflow-hidden ${style.glow}`}
                style={{ border: `1px solid ${style.border}40` }}
              >
                {/* Portrait section */}
                <div className="relative overflow-hidden" style={{ height: '340px' }}>
                  <img
                    src={TORIBIO_PHOTO}
                    alt="Commissioner Toribio Rodriguez"
                    className="w-full h-full object-cover"
                    style={{ objectPosition: '50% 15%' }}
                    data-testid="commissioner-portrait"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a0e12] via-transparent to-transparent" style={{ top: '50%' }} />
                  <div className="absolute bottom-3 left-4 right-4">
                    <h3 className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#E8E0D0] drop-shadow-[0_2px_4px_rgba(0,0,0,0.9)]">
                      Commissioner Toribio Rodriguez
                    </h3>
                    <p className="font-['Outfit'] text-[10px] tracking-widest mt-0.5 drop-shadow-[0_1px_3px_rgba(0,0,0,0.9)]" style={{ color: style.border }}>
                      NYPD — ORGANIZED CRIME TASK FORCE
                    </p>
                  </div>
                </div>

                {/* Message section */}
                <div className="p-5 space-y-4">
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.8 }}
                    className="font-['Outfit'] text-[#E8E0D0]/50 text-xs leading-relaxed"
                  >
                    {trapData.intro}
                  </motion.p>

                  <motion.div
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 1.1 }}
                    className="border-l-2 pl-4"
                    style={{ borderColor: `${style.border}60` }}
                  >
                    <p className="font-['Cormorant_Garamond'] text-[#E8E0D0] text-base italic leading-relaxed">
                      {trapData.message}
                    </p>
                  </motion.div>

                  {/* Escalation consequences warning */}
                  {(trapData.crew_risk || trapData.territory_risk) && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 1.3 }}
                      className="flex items-start gap-2 bg-[#DC2626]/10 border border-[#DC2626]/30 rounded-lg p-3"
                      data-testid="escalation-warning"
                    >
                      <AlertTriangle size={14} className="text-[#DC2626] shrink-0 mt-0.5" />
                      <div className="font-['Outfit'] text-[10px] text-[#DC2626]/80 leading-relaxed">
                        {trapData.crew_risk && trapData.territory_risk
                          ? 'Refusing may result in crew arrests AND territory seizure.'
                          : trapData.crew_risk
                          ? 'Refusing may result in crew member arrests.'
                          : 'Refusing may result in territory seizure.'}
                      </div>
                    </motion.div>
                  )}

                  {/* Choices */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.4 }}
                    className="space-y-2.5 pt-2"
                  >
                    <p className="font-['Outfit'] text-[#E8E0D0]/30 text-[10px] tracking-widest text-center">
                      THE COMMISSIONER DEMANDS A RESPONSE
                    </p>

                    {/* PAY UP button */}
                    <button
                      onClick={() => handleChoice('pay')}
                      disabled={choosing || !canAfford}
                      className={`w-full relative overflow-hidden rounded-lg border p-4 text-left transition-all group ${
                        chosenAction === 'pay'
                          ? 'border-[#C9A84C] bg-[#C9A84C]/20 scale-[0.98]'
                          : canAfford
                            ? 'border-[#C9A84C]/40 hover:border-[#C9A84C] hover:bg-[#C9A84C]/10'
                            : 'border-[#E8E0D0]/10 opacity-50 cursor-not-allowed'
                      }`}
                      data-testid="police-trap-pay-btn"
                    >
                      {chosenAction === 'pay' && (
                        <div className="absolute inset-0 flex items-center justify-center bg-[#C9A84C]/10">
                          <div className="w-5 h-5 border-2 border-[#C9A84C] border-t-transparent rounded-full animate-spin" />
                          <span className="font-['Outfit'] text-[#C9A84C] text-xs tracking-widest ml-3">PAYING BRIBE...</span>
                        </div>
                      )}
                      <div className={`flex items-center justify-between ${chosenAction === 'pay' ? 'opacity-0' : ''}`}>
                        <div className="flex items-center gap-3">
                          <DollarSign size={18} className="text-[#C9A84C]" />
                          <div>
                            <span className="font-['Outfit'] text-sm text-[#C9A84C] tracking-wider block">
                              PAY THE BRIBE
                            </span>
                            <span className="font-['Outfit'] text-[10px] text-[#E8E0D0]/40">
                              {canAfford
                                ? `Make it disappear — $${trapData.bribe_cost.toLocaleString()}`
                                : `Not enough cash — need $${trapData.bribe_cost.toLocaleString()}`
                              }
                            </span>
                          </div>
                        </div>
                        <span className="font-['Outfit'] text-lg font-bold text-[#C9A84C]">
                          -${trapData.bribe_cost.toLocaleString()}
                        </span>
                      </div>
                    </button>

                    {/* TAKE THE HEAT button */}
                    <button
                      onClick={() => handleChoice('heat')}
                      disabled={choosing}
                      className={`w-full relative overflow-hidden rounded-lg border p-4 text-left transition-all group ${
                        chosenAction === 'heat' ? 'scale-[0.98]' : ''
                      }`}
                      style={{ borderColor: chosenAction === 'heat' ? style.border : `${style.border}40`, backgroundColor: chosenAction === 'heat' ? `${style.border}20` : 'transparent' }}
                      onMouseEnter={e => { if (!choosing) e.currentTarget.style.borderColor = style.border; }}
                      onMouseLeave={e => { if (!choosing) e.currentTarget.style.borderColor = `${style.border}40`; }}
                      data-testid="police-trap-heat-btn"
                    >
                      {chosenAction === 'heat' && (
                        <div className="absolute inset-0 flex items-center justify-center" style={{ background: `${style.border}10` }}>
                          <div className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: style.border, borderTopColor: 'transparent' }} />
                          <span className="font-['Outfit'] text-xs tracking-widest ml-3" style={{ color: style.border }}>REFUSING...</span>
                        </div>
                      )}
                      <div className={`flex items-center justify-between ${chosenAction === 'heat' ? 'opacity-0' : ''}`}>
                        <div className="flex items-center gap-3">
                          <Flame size={18} style={{ color: style.border }} />
                          <div>
                            <span className="font-['Outfit'] text-sm tracking-wider block" style={{ color: style.border }}>
                              REFUSE TO PAY
                            </span>
                            <span className="font-['Outfit'] text-[10px] text-[#E8E0D0]/40">
                              {isEscalated ? 'Rodriguez is escalating. This will hurt.' : 'Keep your cash. The Commissioner won\'t forget this.'}
                            </span>
                          </div>
                        </div>
                        <span className="font-['Outfit'] text-lg font-bold" style={{ color: style.border }}>
                          +{trapData.heat_penalty} HEAT
                        </span>
                      </div>
                    </button>
                  </motion.div>
                </div>
              </motion.div>
            </>
          ) : (
            /* Result screen */
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-[#0a0e12] border border-[#1A2535] rounded-xl p-6 text-center space-y-4"
              data-testid="police-trap-result"
            >
              <div className={`w-16 h-16 rounded-full mx-auto flex items-center justify-center ${
                result.type === 'paid' ? 'bg-[#C9A84C]/20' : 'bg-[#8B1A1A]/20'
              }`}>
                {result.type === 'paid' ? (
                  <DollarSign size={28} className="text-[#C9A84C]" />
                ) : (
                  <Flame size={28} className="text-[#8B1A1A]" />
                )}
              </div>
              <h3 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#E8E0D0]">
                {result.type === 'paid' ? 'BRIBE ACCEPTED' : 'HEAT IS RISING'}
              </h3>
              <p className="font-['Outfit'] text-[#E8E0D0]/50 text-xs leading-relaxed">
                {result.narrator}
              </p>
              {/* Extra consequences */}
              {result.consequences?.length > 0 && (
                <div className="space-y-1.5 pt-1">
                  {result.consequences.map((c, i) => (
                    <div key={i} className="flex items-center justify-center gap-2 text-[#DC2626]">
                      {c.type === 'crew_locked' && <><Users size={12} /><span className="font-['Outfit'] text-[10px]">Crew member locked up for {c.hours}h</span></>}
                      {c.type === 'territory_seized' && <><MapPin size={12} /><span className="font-['Outfit'] text-[10px]">Territory seized: {c.name}</span></>}
                    </div>
                  ))}
                </div>
              )}
              <div className="pt-2">
                <span className="font-['Outfit'] text-[#E8E0D0]/20 text-[9px] tracking-widest animate-pulse">
                  RETURNING TO THE STREETS...
                </span>
              </div>
            </motion.div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PoliceTrapPopup;
